import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Header from './components/Header';
import Tabs from './components/Tabs';
import VistaGeneral from './components/VistaGeneral';
import Analytics from './components/Analytics';
import EntradaManual from './components/EntradaManual';
import { loadProjectsFromJson, enrichUnitsWithHubSpot, enrichProjectsWithBC } from './services/dataService';
import projectsJson from './data/projects-properties.json';
import bcJson from './data/bc-gl-entries.json';
import hubspotDealsJson from './data/hubspot-deals.json';

const LS_KEY = 'arras_manual_refunds';

function loadManualRefunds() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveManualRefunds(entries) {
  localStorage.setItem(LS_KEY, JSON.stringify(entries));
}

/**
 * Merges manual refund entries into project data.
 *
 * Each entry: { projectId, date, note, bcRef, rows: [{ unitId, client, address, uniqueId, amount }] }
 *
 * Effects per project:
 *   - desglose.refunds gains one entry per confirmed payment (with per-client breakdown)
 *   - refundPending sums total manual refund amounts
 *   - unit.refundPaid accumulates the amount allocated to that unit
 */
function mergeManualRefunds(projects, manualRefunds) {
  const refundsByProject = {};
  for (const entry of manualRefunds) {
    if (!refundsByProject[entry.projectId]) refundsByProject[entry.projectId] = [];
    refundsByProject[entry.projectId].push(entry);
  }

  return projects.map(project => {
    const entries = refundsByProject[project.id] ?? [];
    if (entries.length === 0) return project;

    const refundDesglose = entries.map(e => ({
      date: e.date,
      amount: -(e.rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)),
      desc: e.note || 'Pago to be refund',
      ref: e.bcRef || null,
      status: 'Pagado',
      manual: true,
      breakdown: e.rows,
    }));

    const refundPendingFromManual = entries.reduce(
      (s, e) => s + e.rows.reduce((rs, r) => rs + (parseFloat(r.amount) || 0), 0),
      0
    );

    const unitRefundMap = {};
    for (const e of entries) {
      for (const r of e.rows) {
        unitRefundMap[r.unitId] = (unitRefundMap[r.unitId] ?? 0) + (parseFloat(r.amount) || 0);
      }
    }

    const enrichedUnits = project.units.map(u => ({
      ...u,
      refundPaid: (u.refundPaid ?? 0) + (unitRefundMap[u.id] ?? 0),
    }));

    return {
      ...project,
      units: enrichedUnits,
      refundPending: (project.refundPending ?? 0) + refundPendingFromManual,
      desglose: {
        ...project.desglose,
        refunds: [...(project.desglose?.refunds ?? []), ...refundDesglose],
      },
    };
  });
}

function App() {
  const [activeTab, setActiveTab] = useState('vista');
  const [baseProjects, setBaseProjects] = useState([]); // projects without manual refunds
  const [manualRefunds, setManualRefunds] = useState(loadManualRefunds);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [hsLoading, setHsLoading] = useState(false);

  useEffect(() => {
    const base = loadProjectsFromJson(projectsJson);
    setLastUpdate(new Date(projectsJson.generatedAt ?? Date.now()));

    const projectsWithBCArras = enrichProjectsWithBC(base, bcJson);
    setBaseProjects(projectsWithBCArras);

    setHsLoading(true);
    new Promise(resolve => setTimeout(resolve, 1500)).then(() => {
      const deals = hubspotDealsJson.deals ?? [];
      const withHS = enrichUnitsWithHubSpot(projectsWithBCArras, deals);
      const withBCFull = enrichProjectsWithBC(withHS, bcJson);
      setBaseProjects(withBCFull);
      setHsLoading(false);
    });
  }, []);

  // Projects with manual refunds merged on top
  const projects = mergeManualRefunds(baseProjects, manualRefunds);

  const handleRefundConfirm = useCallback(({ projectId, date, note, rows }) => {
    const entry = { projectId, date, note, rows, id: Date.now() };
    setManualRefunds(prev => {
      const updated = [...prev, entry];
      saveManualRefunds(updated);
      return updated;
    });
  }, []);

  return (
    <div className="app">
      <div className="wrap">
      <div className="page-label">Prophero · Arras Live · Hackaton Mayo 2026</div>
      <div className="screen">
        <Header projects={projects} lastUpdate={lastUpdate} hsLoading={hsLoading} />
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <div className="tab-content">
          {activeTab === 'vista' && (
            <VistaGeneral
              projects={projects}
              hsLoading={hsLoading}
              onRefundConfirm={handleRefundConfirm}
            />
          )}
          {activeTab === 'analytics' && <Analytics projects={projects} />}
          {activeTab === 'manual' && <EntradaManual projects={projects} />}
        </div>
      </div>
      </div>
    </div>
  );
}

export default App;
